import { useEffect, useRef, memo } from 'react'

import { Parking } from '@/service'
import {
  ParkingWrap,
  ResultsWrapper,
  Container
} from '@/components/parkings/Results.styled'
import { Grid } from '@/styles/grid'
import ParkingTile from '@/components/common/ParkingTile'

import Spinner from '../common/SpinnerPage'

interface Props {
  parkings?: Parking[]
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
}

function Results({
  parkings,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage
}: Props) {
  const observer = useRef<IntersectionObserver | null>(null)
  const lastElementRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isFetchingNextPage) return
    if (observer.current) observer.current.disconnect()

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage && fetchNextPage()
      }
    })

    const currentRef = lastElementRef.current
    if (currentRef) {
      observer.current.observe(currentRef)
    }

    return () => {
      if (observer.current) observer.current.disconnect()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage, parkings])

  return (
    <Grid>
      <Container>
        <ResultsWrapper data-test-id={'results-wrapper'}>
          {parkings?.map((parking, index) => {
            const isLastElement = index === parkings.length - 1
            const isPriority = index === 0

            return (
              <ParkingWrap key={parking.uuid}>
                <div ref={isLastElement ? lastElementRef : null}>
                  <ParkingTile
                    parking={parking}
                    loading={isPriority ? 'eager' : 'lazy'}
                  />
                </div>
              </ParkingWrap>
            )
          })}
        </ResultsWrapper>
        {isFetchingNextPage && <Spinner />}
      </Container>
    </Grid>
  )
}

export default memo(Results)
